import React, { useEffect, useMemo, useState } from 'react';
import { Portal, IconButton, Tooltip, Switch, TextField, CircularProgress } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import AutocompleteSelectFilter from '../../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import styles from './JournalDocumentationModal.module.scss';

const SCOPE_SELECTED = 'selected';
const SCOPE_ALL = 'all';

// Must match JournalDocumentationDateTypes on the server.
const DATE_TYPE_REPORTING = 'reportingmonth';
const DATE_TYPE_REGISTRATION = 'registrationmonth';
const DATE_TYPE_DOCUMENT = 'document';

const DOCUMENT_TYPES = [
    { value: 'incomes', labelKey: 'Incomes', fallback: 'Incomes' },
    { value: 'expenses', labelKey: 'Expenses', fallback: 'Expenses' },
    { value: 'journalentries', labelKey: 'JournalEntries', fallback: 'Journal Entries' },
    { value: 'paymentacceptances', labelKey: 'PaymentAcceptances', fallback: 'Payment Acceptances' },
];

const SORT_BY_OPTIONS = [
    { value: 'uploadNumber', labelKey: 'UploadNumber', fallback: 'Upload Number' },
    { value: 'date', labelKey: 'Date', fallback: 'Date' },
    { value: 'amount', labelKey: 'Amount', fallback: 'Amount' },
    { value: 'reference', labelKey: 'Reference', fallback: 'Reference' },
];

const toDate = (value) => {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toIntOrNull = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
};

const JournalDocumentationModal = ({
    open,
    onClose,
    onSubmit,
    initialTypes = [],
    initialSortBy = 'uploadNumber',
    initialSortDirection = 'asc',
    initialFormat = 'pdf',
    initialStartDate = null,
    initialEndDate = new Date(),
    initialIsRange = false,
    initialIncludeDescription = true,
    selectedRows = [],
    accountCardOptions = [],
    sortCodeOptions = [],
    direction = 'rtl',
    loading = false,
}) => {
    const { t, i18n } = useTranslation();
    const dir = direction || i18n.dir();
    const hasSelectedRows = selectedRows && selectedRows.length > 0;

    const [scope, setScope] = useState(hasSelectedRows ? SCOPE_SELECTED : SCOPE_ALL);
    const [openSections, setOpenSections] = useState({ types: true, output: true, dates: true });

    const [types, setTypes] = useState(new Set(initialTypes));
    const [sortBy, setSortBy] = useState(initialSortBy);
    const [sortDirection, setSortDirection] = useState(initialSortDirection);
    const [format, setFormat] = useState(initialFormat); // 'pdf' | 'excel'
    const [includeDescription, setIncludeDescription] = useState(initialIncludeDescription);

    // The report period is driven by exactly one date field (see dateType).
    const [dateType, setDateType] = useState(DATE_TYPE_REPORTING);
    const [useRange, setUseRange] = useState(!!initialIsRange);
    const [monthFrom, setMonthFrom] = useState(toDate(initialStartDate));
    const [monthTo, setMonthTo] = useState(toDate(initialEndDate) || new Date());
    const [registrationMonthFrom, setRegistrationMonthFrom] = useState(toDate(initialStartDate));
    const [registrationMonthTo, setRegistrationMonthTo] = useState(toDate(initialEndDate) || new Date());
    const [documentDateFrom, setDocumentDateFrom] = useState(toDate(initialStartDate));
    const [documentDateTo, setDocumentDateTo] = useState(toDate(initialEndDate) || new Date());

    const [uploadDateFrom, setUploadDateFrom] = useState(null);
    const [uploadDateTo, setUploadDateTo] = useState(null);

    const [selectedDebitAccounts, setSelectedDebitAccounts] = useState([]);
    const [selectedCreditAccounts, setSelectedCreditAccounts] = useState([]);
    const [selectedSortCodes, setSelectedSortCodes] = useState([]);

    const [referenceFrom, setReferenceFrom] = useState('');
    const [referenceTo, setReferenceTo] = useState('');
    const [confirmationNumber, setConfirmationNumber] = useState('');
    const [uploadNumberFrom, setUploadNumberFrom] = useState('');
    const [uploadNumberTo, setUploadNumberTo] = useState('');
    const [accountCaseId, setAccountCaseId] = useState('');
    const [descriptionContains, setDescriptionContains] = useState('');

    const resetFilters = () => {
        setDateType(DATE_TYPE_REPORTING);
        setUseRange(!!initialIsRange);
        setMonthFrom(toDate(initialStartDate));
        setMonthTo(toDate(initialEndDate) || new Date());
        setRegistrationMonthFrom(toDate(initialStartDate));
        setRegistrationMonthTo(toDate(initialEndDate) || new Date());
        setDocumentDateFrom(toDate(initialStartDate));
        setDocumentDateTo(toDate(initialEndDate) || new Date());
        setUploadDateFrom(null);
        setUploadDateTo(null);
        setSelectedDebitAccounts([]);
        setSelectedCreditAccounts([]);
        setSelectedSortCodes([]);
        setReferenceFrom('');
        setReferenceTo('');
        setConfirmationNumber('');
        setUploadNumberFrom('');
        setUploadNumberTo('');
        setAccountCaseId('');
        setDescriptionContains('');
    };

    useEffect(() => {
        if (!open) return;
        setScope(hasSelectedRows ? SCOPE_SELECTED : SCOPE_ALL);
        setOpenSections({ types: true, output: true, dates: true });
        setTypes(new Set(initialTypes));
        setSortBy(initialSortBy);
        setSortDirection(initialSortDirection);
        setFormat(initialFormat);
        setIncludeDescription(initialIncludeDescription);
        resetFilters();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const textAlign = dir === 'rtl' ? 'right' : 'left';
    const inputProps = useMemo(() => ({ style: { textAlign } }), [textAlign]);
    const standardInputProps = useMemo(() => ({ disableUnderline: true }), []);

    const isSelectedScope = hasSelectedRows && scope === SCOPE_SELECTED;

    // One date field drives the report period; the other two are disabled so they cannot contradict it.
    const dateBases = [
        {
            value: DATE_TYPE_REPORTING,
            label: t('MonthReportDate') || 'Reporting month',
            monthly: true,
            from: monthFrom,
            to: monthTo,
            setFrom: setMonthFrom,
            setTo: setMonthTo,
        },
        {
            value: DATE_TYPE_REGISTRATION,
            label: t('RegistrationMonthDate') || 'Registration month',
            monthly: true,
            from: registrationMonthFrom,
            to: registrationMonthTo,
            setFrom: setRegistrationMonthFrom,
            setTo: setRegistrationMonthTo,
        },
        {
            value: DATE_TYPE_DOCUMENT,
            label: t('DocumentDate') || 'Document date',
            monthly: false,
            from: documentDateFrom,
            to: documentDateTo,
            setFrom: setDocumentDateFrom,
            setTo: setDocumentDateTo,
        },
    ];
    const activeBasis = dateBases.find((basis) => basis.value === dateType) || dateBases[0];

    const accountOptions = useMemo(
        () => accountCardOptions
            .filter((option) => option.cardId)
            .map((option) => ({
                value: option.cardId,
                label: option.accountCaseId ? `${option.name} - ${option.accountCaseId}` : option.name,
            })),
        [accountCardOptions]
    );

    const formattedSortCodeOptions = useMemo(
        () => sortCodeOptions
            .filter((option) => option.sortCodeId)
            .map((option) => ({
                value: option.sortCodeId,
                label: `${option.sortCodeId} - ${option.sortCodeName || ''}`,
            })),
        [sortCodeOptions]
    );

    const uploadDateCount = [uploadDateFrom, uploadDateTo].filter(Boolean).length;
    const accountFilterCount = selectedDebitAccounts.length + selectedCreditAccounts.length + selectedSortCodes.length;
    const additionalFilterCount = [
        referenceFrom,
        referenceTo,
        confirmationNumber,
        uploadNumberFrom,
        uploadNumberTo,
        accountCaseId,
        descriptionContains,
    ].filter((value) => String(value || '').trim() !== '').length;
    const activeFilterCount = uploadDateCount + accountFilterCount + additionalFilterCount;

    const toggleSection = (id) => setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

    const toggleType = (type) => {
        setTypes((prev) => {
            const next = new Set(prev);
            if (next.has(type)) next.delete(type);
            else next.add(type);
            return next;
        });
    };

    const periodEnd = activeBasis.to;
    const periodStart = useRange ? activeBasis.from : activeBasis.to;
    const isValid = isSelectedScope ? true : (types.size > 0 && !!periodEnd);

    const handleSubmit = () => {
        if (!isValid) return;

        if (isSelectedScope) {
            onSubmit({
                types: Array.from(types),
                sortBy,
                sortDirection,
                format,
                startDate: periodStart,
                endDate: periodEnd,
                includeDescription,
                documentIds: selectedRows,
                dateType: DATE_TYPE_REPORTING,
            });
            return;
        }

        onSubmit({
            types: Array.from(types),
            sortBy,
            sortDirection,
            format,
            startDate: periodStart,
            endDate: periodEnd,
            includeDescription,
            documentIds: null,
            // The period is applied to the date field selected above; the other one stays unused.
            dateType,
            uploadDateFrom: uploadDateFrom || null,
            uploadDateTo: uploadDateTo || null,
            reportingMonthFrom: null,
            reportingMonthTo: null,
            debitAccountIds: selectedDebitAccounts.map((account) => account.value ?? account),
            creditAccountIds: selectedCreditAccounts.map((account) => account.value ?? account),
            sortCodeIds: selectedSortCodes.map((sortCode) => sortCode.value ?? sortCode),
            referenceFrom: referenceFrom.trim(),
            referenceTo: referenceTo.trim(),
            confirmationNumber: confirmationNumber.trim(),
            uploadNumberFrom: toIntOrNull(uploadNumberFrom),
            uploadNumberTo: toIntOrNull(uploadNumberTo),
            accountCaseId: accountCaseId.trim(),
            descriptionContains: descriptionContains.trim(),
        });
    };

    const renderSection = ({ id, title, badge, hint, onClear, children }) => {
        const isOpen = !!openSections[id];
        return (
            <section className={`${styles.section}${isOpen ? ` ${styles.sectionOpen}` : ''}`} key={id}>
                <button
                    type="button"
                    className={styles.sectionHeader}
                    onClick={() => toggleSection(id)}
                    aria-expanded={isOpen}
                >
                    <span className={styles.sectionHeaderMain}>
                        <span className={styles.sectionTitle}>{title}</span>
                        {badge > 0 && <span className={styles.sectionBadge}>{badge}</span>}
                    </span>
                    <span className={styles.sectionHeaderSide}>
                        {onClear && badge > 0 && (
                            <span
                                role="button"
                                tabIndex={0}
                                className={styles.clearLink}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onClear();
                                }}
                                onKeyDown={(event) => {
                                    if (event.key !== 'Enter' && event.key !== ' ') return;
                                    event.stopPropagation();
                                    onClear();
                                }}
                            >
                                {t('Clear') || 'Clear'}
                            </span>
                        )}
                        <ExpandMoreIcon
                            fontSize="small"
                            className={`${styles.sectionChevron}${isOpen ? ` ${styles.sectionChevronOpen}` : ''}`}
                        />
                    </span>
                </button>
                {isOpen && (
                    <div className={styles.sectionBody}>
                        {hint && <p className={styles.sectionHint}>{hint}</p>}
                        {children}
                    </div>
                )}
            </section>
        );
    };

    const renderTextField = (label, value, onChange, { type = 'text', min } = {}) => (
        <div className={styles.field}>
            <span className={styles.fieldLabel}>{label}</span>
            <TextField
                value={value}
                onChange={(event) => onChange(event.target.value)}
                fullWidth
                size="small"
                type={type}
                variant="standard"
                placeholder={label}
                className={`${fieldStyles.styleInput} ${styles.compactInput}`}
                InputProps={standardInputProps}
                inputProps={type === 'number' ? { ...inputProps, min } : inputProps}
            />
        </div>
    );

    const renderDateCard = (basis) => {
        const active = basis.value === dateType;
        const renderPicker = (value, onChange) => (basis.monthly
            ? (
                <CustomMonthDatePicker
                    date={value}
                    onDateChange={onChange}
                    direction={dir}
                    height="34px"
                    disabled={!active}
                />
            )
            : (
                <CustomDatePicker
                    date={value}
                    onDateChange={onChange}
                    label=""
                    height="34px"
                    disabled={!active}
                />
            ));
        const toLabel = useRange
            ? (t('EndDate') || 'End Date')
            : (basis.monthly ? (t('Month') || 'Month') : (t('Date') || 'Date'));

        return (
            <div
                key={basis.value}
                className={[
                    styles.dateCard,
                    active ? styles.dateCardActive : styles.dateCardDisabled,
                ].join(' ')}
            >
                <div className={styles.dateCardHeader}>
                    <span className={styles.dateCardTitle}>{basis.label}</span>
                    <Tooltip
                        title={active
                            ? (t('PeriodFilteredByThisField') || 'Period is filtered by this field')
                            : (t('DisabledByPeriodDateField') || 'Disabled — the period is filtered by another date field')}
                    >
                        <span className={`${styles.dateCardTag}${active ? '' : ` ${styles.dateCardTagMuted}`}`}>
                            {active
                                ? <CheckIcon className={styles.lockIcon} />
                                : <LockOutlinedIcon className={styles.lockIcon} />}
                            {active
                                ? (t('Active') || 'Active')
                                : (t('FilterDisabled') || 'Disabled')}
                        </span>
                    </Tooltip>
                </div>
                <div className={styles.gridTwo}>
                    {useRange && (
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>{t('StartDate') || 'Start Date'}</span>
                            {renderPicker(basis.from, basis.setFrom)}
                        </div>
                    )}
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>{toLabel}</span>
                        {renderPicker(basis.to, basis.setTo)}
                    </div>
                </div>
            </div>
        );
    };

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${dir === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={onClose}
            >
                <div dir={dir} onClick={(event) => event.stopPropagation()}>
                    <div className={`${styles.modal}${isSelectedScope ? '' : ` ${styles.modalWide}`}`}>
                        <div className={styles.body}>
                            <div className={styles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={styles.closeIcon}>
                                        <CancelIcon className={styles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t('DownloadReceptionLog') || 'Download Reception Log'}</div>
                                <div />
                            </div>

                            {hasSelectedRows && (
                                <div className={styles.tabs}>
                                    <button
                                        type="button"
                                        className={`${styles.tab}${scope === SCOPE_SELECTED ? ` ${styles.tabActive}` : ''}`}
                                        onClick={() => setScope(SCOPE_SELECTED)}
                                    >
                                        {t('SelectedRowsOnly') || 'Selected rows only'} ({selectedRows.length})
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.tab}${scope === SCOPE_ALL ? ` ${styles.tabActive}` : ''}`}
                                        onClick={() => setScope(SCOPE_ALL)}
                                    >
                                        {t('FullReport') || 'Full report'}
                                    </button>
                                </div>
                            )}

                            <div className={styles.scroll}>
                                {isSelectedScope && (
                                    <div className={styles.notice}>
                                        <InfoOutlinedIcon className={styles.noticeIcon} />
                                        <div>
                                            <p className={styles.noticeTitle}>
                                                {t('SelectedRows') || 'Selected Rows'}: {selectedRows.length}
                                            </p>
                                            <p className={styles.noticeText}>
                                                {t('OnlySelectedRowsWillBeDownloaded')
                                                    || 'Only the selected rows will be included in the download.'}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className={styles.sections}>
                                    {!isSelectedScope && renderSection({
                                        id: 'types',
                                        title: t('DocumentTypes') || 'Document Types',
                                        badge: types.size,
                                        hint: types.size === 0
                                            ? (t('SelectAtLeastOneDocumentType') || 'Select at least one document type')
                                            : null,
                                        children: (
                                            <div className={styles.chipGroup}>
                                                {DOCUMENT_TYPES.map((option) => {
                                                    const selected = types.has(option.value);
                                                    return (
                                                        <button
                                                            type="button"
                                                            key={option.value}
                                                            className={`${styles.chip}${selected ? ` ${styles.chipActive}` : ''}`}
                                                            onClick={() => toggleType(option.value)}
                                                            aria-pressed={selected}
                                                        >
                                                            {selected && <CheckIcon className={styles.chipIcon} />}
                                                            {t(option.labelKey) || option.fallback}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ),
                                    })}

                                    {renderSection({
                                        id: 'output',
                                        title: t('SortingAndFormat') || 'Sorting & Format',
                                        badge: 0,
                                        children: (
                                            <div className={styles.inlineRow}>
                                                <div className={styles.field}>
                                                    <span className={styles.fieldLabel}>{t('Format') || 'Format'}</span>
                                                    <div className={styles.segmented}>
                                                        {['pdf', 'excel'].map((option) => (
                                                            <button
                                                                type="button"
                                                                key={option}
                                                                className={`${styles.segment}${format === option ? ` ${styles.segmentActive}` : ''}`}
                                                                onClick={() => setFormat(option)}
                                                            >
                                                                {option === 'pdf' ? (t('PDF') || 'PDF') : (t('Excel') || 'Excel')}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className={styles.field}>
                                                    <span className={styles.fieldLabel}>{t('SortBy') || 'Sort by'}</span>
                                                    <div className={styles.segmented}>
                                                        {SORT_BY_OPTIONS.map((option) => (
                                                            <button
                                                                type="button"
                                                                key={option.value}
                                                                className={`${styles.segment}${sortBy === option.value ? ` ${styles.segmentActive}` : ''}`}
                                                                onClick={() => setSortBy(option.value)}
                                                            >
                                                                {t(option.labelKey) || option.fallback}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className={styles.field}>
                                                    <span className={styles.fieldLabel}>{t('SortDirection') || t('SortBy') || 'Direction'}</span>
                                                    <div className={styles.segmented}>
                                                        {[
                                                            { value: 'asc', label: t('Ascending') || 'Ascending' },
                                                            { value: 'desc', label: t('Descending') || 'Descending' },
                                                        ].map((option) => (
                                                            <button
                                                                type="button"
                                                                key={option.value}
                                                                className={`${styles.segment}${sortDirection === option.value ? ` ${styles.segmentActive}` : ''}`}
                                                                onClick={() => setSortDirection(option.value)}
                                                            >
                                                                {option.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className={styles.switchRow}>
                                                    <Switch
                                                        size="small"
                                                        checked={includeDescription}
                                                        onChange={(event) => setIncludeDescription(event.target.checked)}
                                                    />
                                                    <span className={styles.switchLabel}>
                                                        {t('IncludeDescription') || 'Include Description'}
                                                    </span>
                                                </div>
                                            </div>
                                        ),
                                    })}

                                    {!isSelectedScope && renderSection({
                                        id: 'dates',
                                        title: t('DateFilters') || 'Date Filters',
                                        badge: uploadDateCount,
                                        hint: t('FilterPeriodByHint')
                                            || 'Pick one date field for the report period. The other date field is disabled so the filters cannot contradict each other.',
                                        onClear: () => {
                                            setUploadDateFrom(null);
                                            setUploadDateTo(null);
                                        },
                                        children: (
                                            <>
                                                <div className={styles.inlineRow}>
                                                    <div className={styles.field}>
                                                        <span className={styles.fieldLabel}>
                                                            {t('FilterPeriodBy') || 'Filter period by'}
                                                        </span>
                                                        <div className={styles.segmented}>
                                                            {dateBases.map((basis) => (
                                                                <button
                                                                    type="button"
                                                                    key={basis.value}
                                                                    className={`${styles.segment}${basis.value === dateType ? ` ${styles.segmentActive}` : ''}`}
                                                                    onClick={() => setDateType(basis.value)}
                                                                >
                                                                    {basis.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <div className={styles.switchRow}>
                                                        <Switch
                                                            size="small"
                                                            checked={useRange}
                                                            onChange={(event) => setUseRange(event.target.checked)}
                                                        />
                                                        <span className={styles.switchLabel}>{t('UseRange') || 'Use range'}</span>
                                                    </div>
                                                </div>

                                                <div className={styles.dateCards}>
                                                    {dateBases.map(renderDateCard)}
                                                </div>

                                                <div className={styles.inlineRow}>
                                                    <div className={`${styles.field} ${styles.fieldSm}`}>
                                                        <span className={styles.fieldLabel}>
                                                            {t('UploadDateFrom') || 'Upload Date From'}
                                                        </span>
                                                        <CustomDatePicker
                                                            date={uploadDateFrom}
                                                            onDateChange={setUploadDateFrom}
                                                            label=""
                                                            height="34px"
                                                            clearable
                                                        />
                                                    </div>
                                                    <div className={`${styles.field} ${styles.fieldSm}`}>
                                                        <span className={styles.fieldLabel}>
                                                            {t('UploadDateTo') || 'Upload Date To'}
                                                        </span>
                                                        <CustomDatePicker
                                                            date={uploadDateTo}
                                                            onDateChange={setUploadDateTo}
                                                            label=""
                                                            height="34px"
                                                            clearable
                                                        />
                                                    </div>
                                                    <p className={`${styles.sectionHint} ${styles.inlineHint}`}>
                                                        {t('UploadDateRangeHint')
                                                            || 'Extra filter, independent of the report period.'}
                                                    </p>
                                                </div>
                                            </>
                                        ),
                                    })}

                                    {!isSelectedScope && renderSection({
                                        id: 'accounts',
                                        title: t('AccountFilters') || 'Account Filters',
                                        badge: accountFilterCount,
                                        onClear: () => {
                                            setSelectedDebitAccounts([]);
                                            setSelectedCreditAccounts([]);
                                            setSelectedSortCodes([]);
                                        },
                                        children: (
                                            <div className={styles.grid}>
                                                <div className={styles.field}>
                                                    <span className={styles.fieldLabel}>
                                                        {t('DebitAccount') || 'Debit Account'}
                                                    </span>
                                                    <AutocompleteSelectFilter
                                                        options={accountOptions}
                                                        selectedValues={selectedDebitAccounts}
                                                        onChange={(_, value) => setSelectedDebitAccounts(value || [])}
                                                        placeholder={t('SelectDebitAccounts') || 'Select Debit Accounts'}
                                                        direction={dir}
                                                        searchable
                                                        portalMenu
                                                    />
                                                </div>
                                                <div className={styles.field}>
                                                    <span className={styles.fieldLabel}>
                                                        {t('CreditAccount') || 'Credit Account'}
                                                    </span>
                                                    <AutocompleteSelectFilter
                                                        options={accountOptions}
                                                        selectedValues={selectedCreditAccounts}
                                                        onChange={(_, value) => setSelectedCreditAccounts(value || [])}
                                                        placeholder={t('SelectCreditAccounts') || 'Select Credit Accounts'}
                                                        direction={dir}
                                                        searchable
                                                        portalMenu
                                                    />
                                                </div>
                                                <div className={styles.field}>
                                                    <span className={styles.fieldLabel}>
                                                        {t('SortCodeFilter') || 'Sort Code Filter'}
                                                    </span>
                                                    <AutocompleteSelectFilter
                                                        options={formattedSortCodeOptions}
                                                        selectedValues={selectedSortCodes}
                                                        onChange={(_, value) => setSelectedSortCodes(value || [])}
                                                        placeholder={t('SelectSortCodes') || 'Select Sort Codes'}
                                                        direction={dir}
                                                        searchable
                                                        portalMenu
                                                    />
                                                </div>
                                            </div>
                                        ),
                                    })}

                                    {!isSelectedScope && renderSection({
                                        id: 'additional',
                                        title: t('AdditionalFilters') || 'Additional Filters',
                                        badge: additionalFilterCount,
                                        onClear: () => {
                                            setReferenceFrom('');
                                            setReferenceTo('');
                                            setConfirmationNumber('');
                                            setUploadNumberFrom('');
                                            setUploadNumberTo('');
                                            setAccountCaseId('');
                                            setDescriptionContains('');
                                        },
                                        children: (
                                            <div className={styles.grid}>
                                                {renderTextField(t('ReferenceFrom') || 'Reference From', referenceFrom, setReferenceFrom)}
                                                {renderTextField(t('ReferenceTo') || 'Reference To', referenceTo, setReferenceTo)}
                                                {renderTextField(
                                                    t('UploadNumberFrom') || 'Upload Number From',
                                                    uploadNumberFrom,
                                                    setUploadNumberFrom,
                                                    { type: 'number', min: 0 }
                                                )}
                                                {renderTextField(
                                                    t('UploadNumberTo') || 'Upload Number To',
                                                    uploadNumberTo,
                                                    setUploadNumberTo,
                                                    { type: 'number', min: 0 }
                                                )}
                                                {renderTextField(
                                                    t('ConfirmationNumber') || 'Confirmation Number',
                                                    confirmationNumber,
                                                    setConfirmationNumber
                                                )}
                                                {renderTextField(t('AccountCaseId') || 'Account Case ID', accountCaseId, setAccountCaseId)}
                                                {renderTextField(
                                                    t('DescriptionContains') || 'Description Contains',
                                                    descriptionContains,
                                                    setDescriptionContains
                                                )}
                                            </div>
                                        ),
                                    })}
                                </div>
                            </div>

                            <div className={styles.footer}>
                                {!isSelectedScope && (
                                    <button
                                        type="button"
                                        className={`${styles.ghostBtn} ${styles.footerSpacer}`}
                                        onClick={resetFilters}
                                        disabled={activeFilterCount === 0}
                                    >
                                        {t('ResetFilters') || 'Reset filters'}
                                        {activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                                    </button>
                                )}
                                <button type="button" className={styles.secondaryBtn} onClick={onClose}>
                                    {t('cancelButton') || 'Cancel'}
                                </button>
                                <button
                                    type="button"
                                    className={styles.primaryBtn}
                                    onClick={handleSubmit}
                                    disabled={!isValid || loading}
                                >
                                    {loading
                                        ? <CircularProgress className={styles.spinner} size={18} color="inherit" />
                                        : (t('Download') || 'Download')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default JournalDocumentationModal;
